/**
 * AttachMeetingDialog — search-and-pick UI for manually attaching a
 * meeting the auto-matcher missed to a job (CSR command-centre manual-link
 * work — mirrors ``AttachEmailDialog``). Opened from JobCommsSection's
 * "Attach a meeting" control.
 *
 * Searches the job's customer's meetings via the same read the
 * Communications page uses (`DataAdapter.listCommunications`, scoped by
 * `customerId` + `types: ["meeting"]`) — no bespoke backend surface.
 * Meetings carry no body/snippet (see ``MeetingLink``'s docstring in
 * adapter.ts), so the search is title-only — the mock's `q` filter for
 * meeting rows already only matches `title` (see mock-adapter.ts's
 * `listCommunications`), so no extra filtering is needed here. Picking a
 * result calls back to the caller (`onAttach`), which owns the actual
 * `linkMeetingToJob` write and the optimistic-insert/rollback pattern
 * shared with JobCommsSection's other actions.
 *
 * Meetings already shown on this job can't be excluded precisely, for the
 * same reason `AttachEmailDialog` can't: the composed job-comms view never
 * exposes the underlying meeting id, only the link id. Instead, a
 * candidate whose title matches a row already on this job is marked (not
 * hidden) via `alreadyLinkedKeys` — a best-effort dedup hint, keyed on
 * title alone since meetings have no second field (like email's
 * from_address) to pair it with.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../primitives/command";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { CommunicationItem } from "@shared-core/mock-data/communications";

export interface MeetingCandidate {
  id: string;
  title: string;
  start_at: string;
  end_at: string;
  location: string | null;
}

/** Identity key used for the best-effort "already on this job" marking —
 *  see file header. */
export function meetingIdentityKey(title: string): string {
  return title.trim().toLowerCase();
}

interface AttachMeetingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The job's customer — scopes the search to one customer's meetings. */
  customerId: string;
  /** Identity keys (see `meetingIdentityKey`) of meetings already shown on
   *  this job, for the "already attached" marker. */
  alreadyLinkedKeys: Set<string>;
  /** Performs the actual attach write. The dialog awaits this before
   *  closing so it can show an "Attaching…" state on the picked row. */
  onAttach: (candidate: MeetingCandidate) => Promise<void>;
  /** Pre-seeds the "attaching" row for Storybook — not used on the page. */
  initialAttachingId?: string;
}

const SEARCH_DEBOUNCE_MS = 250;
const RESULTS_PAGE_SIZE = 20;

function isMeetingItem(
  item: CommunicationItem,
): item is CommunicationItem & { type: "meeting" } {
  return item.type === "meeting";
}

function formatCandidateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function AttachMeetingDialog({
  open,
  onOpenChange,
  customerId,
  alreadyLinkedKeys,
  onAttach,
  initialAttachingId,
}: AttachMeetingDialogProps) {
  const dataAdapter = useDataAdapter();
  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState<MeetingCandidate[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachingId, setAttachingId] = useState<string | null>(
    initialAttachingId ?? null,
  );
  const fetchSeq = useRef(0);

  const runSearch = useCallback(
    async (q: string) => {
      const seq = ++fetchSeq.current;
      setLoading(true);
      setError(null);
      try {
        const result = await dataAdapter.listCommunications({
          customerId,
          types: ["meeting"],
          q: q || undefined,
          pageSize: RESULTS_PAGE_SIZE,
        });
        if (seq !== fetchSeq.current) return;
        setCandidates(
          result.items.filter(isMeetingItem).map((item) => ({
            id: item.data.id,
            title: item.data.title,
            start_at: item.data.start_at,
            end_at: item.data.end_at,
            location: item.data.location,
          })),
        );
      } catch (err) {
        if (seq !== fetchSeq.current) return;
        setError(err instanceof Error ? err.message : "Search failed");
        setCandidates([]);
      } finally {
        if (seq === fetchSeq.current) setLoading(false);
      }
    },
    [dataAdapter, customerId],
  );

  // Reset and load the customer's most recent meetings as soon as the dialog opens.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setCandidates(null);
    setAttachingId(initialAttachingId ?? null);
    void runSearch("");
    // Only on open — the debounced effect below handles subsequent typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Debounced re-search while the dialog stays open and the query changes.
  useEffect(() => {
    if (!open || candidates === null) return;
    const handle = setTimeout(
      () => void runSearch(query.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  async function handleSelect(candidate: MeetingCandidate) {
    if (attachingId) return;
    setAttachingId(candidate.id);
    try {
      await onAttach(candidate);
    } finally {
      setAttachingId(null);
      onOpenChange(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 overflow-hidden sm:max-w-lg">
        <DialogHeader className="p-4 pb-0">
          <DialogTitle>Attach a meeting</DialogTitle>
          <DialogDescription>
            Search this customer&apos;s meetings and pick one to link to this
            job.
          </DialogDescription>
        </DialogHeader>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by title…"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList className="max-h-[360px]">
            {loading && (
              <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                Searching…
              </div>
            )}
            {error && !loading && (
              <div className="px-4 py-6 text-sm text-destructive">{error}</div>
            )}
            {!loading && !error && candidates && candidates.length === 0 && (
              <CommandEmpty>
                No meetings found{query.trim() ? ` for "${query.trim()}"` : ""}.
              </CommandEmpty>
            )}
            {!loading && !error && candidates && candidates.length > 0 && (
              <CommandGroup>
                {candidates.map((candidate) => {
                  const alreadyLinked = alreadyLinkedKeys.has(
                    meetingIdentityKey(candidate.title),
                  );
                  const isAttaching = attachingId === candidate.id;
                  return (
                    <CommandItem
                      key={candidate.id}
                      value={candidate.id}
                      disabled={alreadyLinked || attachingId !== null}
                      onSelect={() => {
                        if (alreadyLinked) return;
                        void handleSelect(candidate);
                      }}
                      className="flex-col items-stretch gap-0.5 py-2"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium truncate">
                          {candidate.title}
                        </span>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {formatCandidateTime(candidate.start_at)}
                        </span>
                      </div>
                      {candidate.location && (
                        <span className="text-xs text-muted-foreground truncate">
                          {candidate.location}
                        </span>
                      )}
                      {isAttaching && (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                          <Loader2 className="size-3 animate-spin" />
                          Attaching…
                        </span>
                      )}
                      {!isAttaching && alreadyLinked && (
                        <span className="text-xs text-emerald-600 mt-0.5">
                          Already attached to this job
                        </span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
